import { useMemo } from 'react';
import { useMatches, Link } from '@tanstack/react-router';
import type { ComponentPropsWithoutRef } from 'react';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import { useTranslation } from '@/hooks/useTranslation';
import styles from './Breadcrumbs.module.scss';

// Ensure staticData type augmentation is loaded
import '@lib/routerTypes';

/** A single breadcrumb item. */
export interface BreadcrumbItem {
  /** Display label for this breadcrumb segment. */
  label: string;
  /** Path to link to. If undefined, rendered as plain text (current page). */
  path?: string;
}

/** Props for the Breadcrumbs component. */
export interface BreadcrumbsProps {
  /** Override items instead of reading from route matches. Useful for standalone usage (e.g. showcase). */
  items?: BreadcrumbItem[];
  /** Additional CSS class applied to the root `nav` element. */
  className?: string;
}

/**
 * Shared breadcrumb navigation component.
 *
 * When `items` is provided, renders those directly.
 * Otherwise, derives breadcrumbs from the current route match chain
 * using `staticData.breadcrumbKey` (translated) or `staticData.breadcrumb` (plain) on each matched route.
 */
function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  const matches = useMatches();
  const overrides = useBreadcrumbOverride((s) => s.overrides);
  const { t } = useTranslation();

  const crumbs = useMemo<BreadcrumbItem[]>(() => {
    if (items) return items;

    return matches
      .filter((m) => {
        const sd = m.staticData as Record<string, unknown> | undefined;
        return sd?.breadcrumb || sd?.breadcrumbKey;
      })
      .map((m) => {
        const sd = m.staticData as Record<string, unknown>;
        const override = overrides.get(m.pathname);
        const breadcrumbKey = sd.breadcrumbKey as string | undefined;
        const breadcrumbFallback = sd.breadcrumb as string;
        const label = override ?? (breadcrumbKey ? t(breadcrumbKey, breadcrumbFallback) : breadcrumbFallback);
        return {
          label,
          path: m.pathname,
        };
      });
  }, [items, matches, overrides, t]);

  if (crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={`${styles.root}${className ? ` ${className}` : ''}`}
    >
      <ol className={styles.crumbList}>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;

          return (
            <li key={crumb.path ?? crumb.label} className={styles.crumbItem}>
              {index > 0 && (
                <Separator />
              )}
              {isLast || !crumb.path ? (
                <span className={styles.current} aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.path} className={styles.link}>
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Separator character between breadcrumb segments. */
function Separator(props: ComponentPropsWithoutRef<'span'>) {
  return (
    <span aria-hidden="true" className={styles.separator} {...props}>
      /
    </span>
  );
}

export default Breadcrumbs;
